import { authenticatedFetch } from './auth.service';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://admin.goridesrw.com/api/remote";

const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
});

const getMultipartAuthHeaders = () => ({});

const handleResponse = async (response) => {
    if (!response.ok) {
        let errorMsg = 'Failed to fetch data';
        try {
            const data = await response.json();
            if (data.message) errorMsg = data.message;
        } catch (e) {
            // ignore
        }
        throw new Error(errorMsg);
    }
    return response.json();
};

export const ownerService = {
    getDashboardStats: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/dashboard/stats`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },
    getRecentActivities: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/dashboard/activity`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },
    getUpcomingBookings: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/dashboard/bookings`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },
    getOwnerBookings: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/bookings`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },
    getSettings: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/settings`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },
    getBanks: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/settings/banks`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },
    updateSettingsProfile: async (data) => {
        const response = await authenticatedFetch(`${BASE_URL}/settings/profile`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    uploadIdentificationProof: async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await authenticatedFetch(`${BASE_URL}/customer-verification/identification-proof`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData
        });
        return handleResponse(response);
    },
    uploadPartnershipContract: async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await authenticatedFetch(`${BASE_URL}/customer-verification/partnership-contract`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData
        });
        return handleResponse(response);
    },
    updateSettingsSecurity: async (data) => {
        const response = await authenticatedFetch(`${BASE_URL}/settings/security`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    updateSettingsNotifications: async (data) => {
        const response = await authenticatedFetch(`${BASE_URL}/settings/notifications`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    updateSettingsPayout: async (data) => {
        const response = await authenticatedFetch(`${BASE_URL}/settings/payout`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    getRevenueData: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/dashboard/revenue`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },
    getOwnerFleet: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/fleet`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch fleet data');
        return response.json();
    },

    getOwnerCarDetails: async (id) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/fleet/${id}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getOwnerFleetAnalytics: async (id) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/fleet/${id}/analytics`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    removeFleetFromPlatform: async (id) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/fleet/${id}/remove`, {
            method: 'PATCH',
            headers: getAuthHeaders()
        });
        if (!response.ok) {
            throw new Error('Failed to remove vehicle from platform');
        }
    },

    updateFleetStatus: async (id, data) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/fleet/${id}/status`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            throw new Error('Failed to update vehicle availability');
        }
    },

    updateFleetPricing: async (id, data) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/fleet/${id}/pricing`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            let errorMsg = 'Failed to update vehicle pricing';
            try {
                const payload = await response.json();
                if (payload.message) {
                    errorMsg = payload.message;
                }
            } catch (e) {
                // ignore
            }
            throw new Error(errorMsg);
        }
    },

    deleteFleet: async (id) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/fleet/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!response.ok) {
            throw new Error('Failed to delete vehicle');
        }
    },

    getCarOptions: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/options`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch car options');
        return response.json();
    },

    getDrafts: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/drafts`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch drafts');
        return response.json();
    },

    getDraftById: async (id) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    createDraft: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/draft`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to create draft');
        return response.json();
    },

    updateDraftStep1: async (id, data) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/step1`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to update step 1');
        return response.json();
    },

    updateDraftStep2: async (id, data) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/step2`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to update step 2');
        return response.json();
    },

    updateDraftStep3: async (id, data) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/step3`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to update step 3');
        return response.json();
    },

    submitDraft: async (id) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/submit`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to submit draft');
        return response.json();
    },

    uploadDocument: async (id, requiredDocumentId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('requiredDocumentId', requiredDocumentId);

        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/documents`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData
        });
        return handleResponse(response);
    },

    deleteDocument: async (id, docId) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/documents/${docId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    uploadPrimaryImage: async (id, file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/images/primary`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData
        });
        return handleResponse(response);
    },

    uploadImages: async (id, files) => {
        const formData = new FormData();
        Array.from(files).forEach(file => {
            formData.append('files', file);
        });

        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/images`, {
            method: 'POST',
            headers: getMultipartAuthHeaders(),
            body: formData
        });
        return handleResponse(response);
    },

    deleteImage: async (id, imageId) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/cars/${id}/images/${imageId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleResponse(response);
    },

    getFinanceStats: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/finances/stats`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },

    getPayoutRequests: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/finances/payout-requests`, { headers: getAuthHeaders() });
        return handleResponse(response);
    },

    createPayoutRequest: async (data) => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/finances/payout-requests`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    exportPayoutRequests: async () => {
        const response = await authenticatedFetch(`${BASE_URL}/owner/finances/payout-requests/export`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) {
            throw new Error('Failed to export payout requests');
        }
        return response.blob();
    },

    getTransactions: async (params) => {
        const query = new URLSearchParams();
        if (params) {
            Object.keys(params).forEach(key => {
                if (params[key] !== undefined && params[key] !== null) {
                    query.append(key, params[key]);
                }
            });
        }
        const response = await authenticatedFetch(`${BASE_URL}/owner/finances/transactions?${query.toString()}`, { headers: getAuthHeaders() });
        return handleResponse(response);
    }
};
